import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DateInput } from "../components/ui/date-input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import DataTable from "../components/ui/data-table";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { 
  Loader2, Plus, Pencil, Trash2, MapPin, ArrowRight, History, 
  FileText, AlertTriangle, Package, Truck, RefreshCw
} from "lucide-react";
import { useTranslation } from "../contexts/I18nContext";

export default function AssetSpostamentiPage() {
  const { t } = useTranslation();
  const [spostamenti, setSpostamenti] = useState([]);
  const [asset, setAsset] = useState([]);
  const [centriCosto, setCentriCosto] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  
  // Selezione multipla asset per nuovo spostamento
  const [selectedAssets, setSelectedAssets] = useState(new Set());
  const [filterCdc, setFilterCdc] = useState("__all__");
  
  // Form per spostamento massivo
  const [form, setForm] = useState({
    data_spostamento: new Date().toISOString().split('T')[0],
    centro_costo_destinazione_id: "",
    causale_trasporto: "Trasferimento interno",
    trasporto_a_cura: "Interna",
    vettore: "",
    aspetto_esteriore: "Misto",
    note: ""
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const prefix = getApiPrefix();
      const [spostRes, assetRes, ccRes] = await Promise.all([
        api.get(`${prefix}/asset/spostamenti`),
        api.get(`${prefix}/asset`),
        api.get(`${prefix}/cost-centers`)
      ]);
      setSpostamenti(spostRes.data || []);
      setAsset(assetRes.data || []);
      setCentriCosto(ccRes.data || []);
    } catch (err) {
      handleApiError(err, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Raggruppa asset per CDC di origine
  const assetPerCdc = useMemo(() => {
    const grouped = {};
    asset.forEach(a => {
      const cdcId = a.centro_costo_id || "__none__";
      if (!grouped[cdcId]) grouped[cdcId] = [];
      grouped[cdcId].push(a);
    });
    return grouped;
  }, [asset]);

  // Asset filtrati per CDC selezionato
  const assetFiltrati = useMemo(() => {
    if (filterCdc === "__all__") return asset;
    if (filterCdc === "__none__") return asset.filter(a => !a.centro_costo_id);
    return asset.filter(a => a.centro_costo_id === filterCdc);
  }, [asset, filterCdc]);

  // CDC di origine degli asset selezionati (deve essere unico)
  const cdcOrigineSelezionati = useMemo(() => {
    if (selectedAssets.size === 0) return null;
    const cdcs = new Set();
    selectedAssets.forEach(id => {
      const a = asset.find(x => x.id === id);
      if (a) cdcs.add(a.centro_costo_id || "__none__");
    });
    if (cdcs.size > 1) return "__multiple__"; // Errore: CDC multipli
    return Array.from(cdcs)[0];
  }, [selectedAssets, asset]);

  // Toggle selezione asset
  const toggleAsset = (assetId) => {
    const a = asset.find(x => x.id === assetId);
    if (!a) return;
    
    const newSet = new Set(selectedAssets);
    
    if (newSet.has(assetId)) {
      newSet.delete(assetId);
    } else {
      // Verifica che il CDC sia compatibile con quelli già selezionati
      if (newSet.size > 0) {
        const cdcAsset = a.centro_costo_id || "__none__";
        const firstAsset = asset.find(x => x.id === Array.from(newSet)[0]);
        const cdcFirst = firstAsset?.centro_costo_id || "__none__";
        
        if (cdcAsset !== cdcFirst) {
          handleApiError({ message: t("errors.assetsSameOrigin") });
          return;
        }
      }
      newSet.add(assetId);
    }
    
    setSelectedAssets(newSet);
  };

  // Seleziona tutti gli asset visibili (stesso CDC)
  const selectAllVisible = () => {
    if (filterCdc === "__all__") {
      handleApiError({ message: t("errors.filterByCdc") });
      return;
    }
    const newSet = new Set();
    assetFiltrati.forEach(a => newSet.add(a.id));
    setSelectedAssets(newSet);
  };

  // Apri dialog nuovo spostamento
  const openAddDialog = () => {
    if (selectedAssets.size === 0) {
      handleApiError({ message: "Seleziona almeno un asset" });
      return;
    }
    if (cdcOrigineSelezionati === "__multiple__") {
      handleApiError({ message: t("errors.assetsDifferentCdc") });
      return;
    }
    setForm({
      data_spostamento: new Date().toISOString().split('T')[0],
      centro_costo_destinazione_id: "",
      causale_trasporto: "Trasferimento interno",
      trasporto_a_cura: "Interna",
      vettore: "",
      aspetto_esteriore: "Misto",
      note: ""
    });
    setDialogOpen(true);
  };

  // Crea spostamento massivo con DDT automatico
  const handleSubmit = async () => {
    if (selectedAssets.size === 0) {
      handleApiError({ message: "Seleziona almeno un asset" });
      return;
    }
    if (!form.data_spostamento || !form.centro_costo_destinazione_id) {
      handleApiError({ message: t("errors.requiredDateDestination") });
      return;
    }
    
    // Verifica che la destinazione sia diversa dall'origine
    if (form.centro_costo_destinazione_id === cdcOrigineSelezionati) {
      handleApiError({ message: t("errors.differentDestination") });
      return;
    }
    
    try {
      const prefix = getApiPrefix();
      const params = new URLSearchParams();
      
      // Asset IDs
      selectedAssets.forEach(id => params.append("asset_ids", id));
      
      // Dati spostamento
      params.append("data_spostamento", form.data_spostamento);
      params.append("centro_costo_destinazione_id", form.centro_costo_destinazione_id);
      if (cdcOrigineSelezionati && cdcOrigineSelezionati !== "__none__") {
        params.append("centro_costo_origine_id", cdcOrigineSelezionati);
      }
      if (form.note) params.append("note", form.note);
      
      // Dati DDT
      params.append("causale_trasporto", form.causale_trasporto);
      params.append("trasporto_a_cura", form.trasporto_a_cura);
      if (form.vettore) params.append("vettore", form.vettore);
      params.append("aspetto_esteriore", form.aspetto_esteriore);
      
      const res = await api.post(`${prefix}/asset/spostamenti/massivo?${params.toString()}`);
      
      showSuccess(res.data.message || `Spostamento creato con DDT n. ${res.data.numero_ddt}`);
      setDialogOpen(false);
      setSelectedAssets(new Set());
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.errorCreateTransfer"));
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`${getApiPrefix()}/asset/spostamenti/${deleteDialog.item.id}`);
      showSuccess(t("success.transferDeleted"));
      setDeleteDialog({ open: false, item: null });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    }
  };

  // Nome CDC per ID
  const getCdcNome = (cdcId) => {
    if (!cdcId || cdcId === "__none__") return "Non assegnato";
    const cc = centriCosto.find(c => c.id === cdcId);
    return cc?.nome || "N/A";
  };

  // Stats
  const totaleSpostamenti = spostamenti.length;
  const assetSpostati = new Set(spostamenti.map(s => s.asset_id)).size;

  // Colonne tabella storico
  const columns = [
    {
      key: 'ddt_numero',
      label: 'DDT',
      render: (value, row) => row.ddt_id ? (
        <Badge variant="secondary" className="font-mono">{value || 'N/A'}</Badge>
      ) : <span className="text-muted-foreground">-</span>
    },
    {
      key: 'n_asset_interno',
      label: 'N° Asset',
      render: (value) => value ? (
        <span className="font-mono text-sm bg-muted px-2 py-0.5 rounded">{value}</span>
      ) : <span className="text-muted-foreground">-</span>
    },
    {
      key: 'asset_info',
      label: 'Asset',
      getValue: (row) => `${row.tipologia || ''} ${row.marca || ''} ${row.modello || ''}`,
      render: (value, row) => (
        <div>
          <span className="font-medium">{row.tipologia}</span>
          <div className="text-xs text-muted-foreground">{row.marca} {row.modello}</div>
        </div>
      )
    },
    { key: 'data_spostamento', label: t('common.date') },
    {
      key: 'da_cdc_nome',
      label: 'Da',
      render: (v) => v || <span className="text-muted-foreground italic">N/A</span>
    },
    {
      key: 'arrow',
      label: '',
      sortable: false,
      render: () => <ArrowRight className="h-4 w-4 text-muted-foreground" />
    },
    {
      key: 'a_cdc_nome',
      label: 'A',
      render: (v) => v || <span className="text-muted-foreground italic">N/A</span>
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      render: (_, row) => (
        <Button variant="ghost" size="icon" onClick={() => setDeleteDialog({ open: true, item: row })}>
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      )
    }
  ];

  return (
    <div className="space-y-6" data-testid="asset-spostamenti-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
              <Truck className="h-5 w-5 text-white" />
            </div>
            Spostamenti Asset
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Registra spostamenti con generazione automatica DDT</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />{t('common.refresh')}</Button>
        </div>
      </div>

      {/* KPI */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Spostamenti Totali</p>
                <p className="text-2xl font-bold">{totaleSpostamenti}</p>
              </div>
              <History className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Asset Movimentati</p>
                <p className="text-2xl font-bold">{assetSpostati}</p>
              </div>
              <Package className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card className={selectedAssets.size > 0 ? "border-green-500 bg-green-50" : ""}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Asset Selezionati</p>
                <p className="text-2xl font-bold text-green-600">{selectedAssets.size}</p>
                {selectedAssets.size > 0 && (
                  <p className="text-xs text-green-600">
                    Da: {getCdcNome(cdcOrigineSelezionati)}
                  </p>
                )}
              </div>
              <Button 
                onClick={openAddDialog} 
                disabled={selectedAssets.size === 0}
                className="gap-2"
              >
                <Truck className="h-4 w-4" />
                Sposta
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sezione Selezione Asset */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Package className="h-4 w-4" />
                Seleziona Asset da Spostare
              </CardTitle>
              <CardDescription>
                Seleziona gli asset che vuoi spostare. Devono partire tutti dallo stesso CDC.
              </CardDescription>
            </div>
            <div className="flex gap-2 items-center">
              <Label className="text-xs">Filtra per CDC:</Label>
              <Select value={filterCdc} onValueChange={(v) => { setFilterCdc(v); setSelectedAssets(new Set()); }}>
                <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tutti i CDC</SelectItem>
                  <SelectItem value="__none__">Non assegnati</SelectItem>
                  {centriCosto.map(cc => (
                    <SelectItem key={cc.id} value={cc.id}>{cc.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {filterCdc !== "__all__" && (
                <Button variant="outline" size="sm" onClick={selectAllVisible}>{t('common.selectAllItems')}</Button>
              )}
              {selectedAssets.size > 0 && (
                <Button variant="ghost" size="sm" onClick={() => setSelectedAssets(new Set())}>{t('common.deselect')}</Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : assetFiltrati.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">Nessun asset trovato</p>
          ) : (
            <div className="border rounded-lg max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12"></TableHead>
                    <TableHead>{t('common.assetNumber')}</TableHead>
                    <TableHead>{t('common.typology')}</TableHead>
                    <TableHead>{t("common.description")}</TableHead>
                    <TableHead>Seriale/Matricola</TableHead>
                    <TableHead>CDC Attuale</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assetFiltrati.map(a => {
                    const isSelected = selectedAssets.has(a.id);
                    const cdcAsset = a.centro_costo_id || "__none__";
                    const canSelect = selectedAssets.size === 0 || cdcOrigineSelezionati === cdcAsset;
                    
                    return (
                      <TableRow 
                        key={a.id} 
                        className={isSelected ? "bg-green-50" : canSelect ? "hover:bg-muted/50 cursor-pointer" : "opacity-50"}
                        onClick={() => canSelect && toggleAsset(a.id)}
                      >
                        <TableCell>
                          <Checkbox 
                            checked={isSelected}
                            disabled={!canSelect && !isSelected}
                            onCheckedChange={() => toggleAsset(a.id)}
                          />
                        </TableCell>
                        <TableCell className="font-mono">{a.n_asset_interno || '-'}</TableCell>
                        <TableCell className="font-medium">{a.tipologia_nome || a.tipologia || '-'}</TableCell>
                        <TableCell>
                          <div>{a.marca} {a.modello}</div>
                          <div className="text-xs text-muted-foreground">{a.descrizione}</div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{a.numero_seriale || a.matricola || '-'}</TableCell>
                        <TableCell>
                          <Badge variant={a.centro_costo_id ? "default" : "outline"}>
                            {a.centro_costo_nome || "Non assegnato"}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Storico Spostamenti */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <History className="h-4 w-4" />{t('common.movementHistory')}</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            data={spostamenti}
            columns={columns}
            searchable
            searchPlaceholder={t("common.searchPlaceholder")}
            paginated
            pageSize={10}
          />
        </CardContent>
      </Card>

      {/* Dialog Nuovo Spostamento */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Nuovo Spostamento con DDT
            </DialogTitle>
            <DialogDescription>
              Verrà generato automaticamente un DDT per questo spostamento
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Riepilogo asset selezionati */}
            <div className="p-3 bg-muted rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">{selectedAssets.size} Asset selezionati</span>
                <Badge>{getCdcNome(cdcOrigineSelezionati)}</Badge>
              </div>
              <div className="text-sm text-muted-foreground max-h-20 overflow-y-auto">
                {Array.from(selectedAssets).map(id => {
                  const a = asset.find(x => x.id === id);
                  return a ? (
                    <span key={id} className="inline-block mr-2 mb-1 px-2 py-0.5 bg-background rounded text-xs">
                      {a.n_asset_interno || a.codice} - {a.tipologia_nome || a.tipologia}
                    </span>
                  ) : null;
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data Spostamento *</Label>
                <DateInput
                  value={form.data_spostamento}
                  onChange={(v) => setForm({...form, data_spostamento: v})}
                />
              </div>
              <div className="space-y-2">
                <Label>Destinazione (CDC) *</Label>
                <Select 
                  value={form.centro_costo_destinazione_id} 
                  onValueChange={(v) => setForm({...form, centro_costo_destinazione_id: v})}
                >
                  <SelectTrigger><SelectValue placeholder="Seleziona destinazione..." /></SelectTrigger>
                  <SelectContent>
                    {centriCosto
                      .filter(cc => cc.id !== cdcOrigineSelezionati)
                      .map(cc => (
                        <SelectItem key={cc.id} value={cc.id}>{cc.nome}</SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Campi DDT */}
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Dettagli DDT
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('common.transportReason')}</Label>
                  <Input 
                    value={form.causale_trasporto}
                    onChange={(e) => setForm({...form, causale_trasporto: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('common.transportBy')}</Label>
                  <Select value={form.trasporto_a_cura} onValueChange={(v) => setForm({...form, trasporto_a_cura: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Interna">{t('common.internal')}</SelectItem>
                      <SelectItem value="Mittente">{t('common.sender')}</SelectItem>
                      <SelectItem value="Destinatario">{t('common.recipient')}</SelectItem>
                      <SelectItem value="Vettore">{t('common.carrier')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('common.carrier')}</Label>
                  <Input 
                    value={form.vettore}
                    onChange={(e) => setForm({...form, vettore: e.target.value})}
                    placeholder="Nome vettore (opzionale)"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('common.appearance')}</Label>
                  <Input 
                    value={form.aspetto_esteriore}
                    onChange={(e) => setForm({...form, aspetto_esteriore: e.target.value})}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("common.notes")}</Label>
              <Textarea 
                value={form.note}
                onChange={(e) => setForm({...form, note: e.target.value})}
                placeholder={t("common.notes")}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleSubmit} disabled={!form.centro_costo_destinazione_id}>
              <FileText className="h-4 w-4 mr-2" />
              Crea Spostamento + DDT
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Conferma Eliminazione */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, item: deleteDialog.item })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("confirm.confirmDeletion")}</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare questo spostamento?
              {deleteDialog.item?.ddt_id && (
                <span className="block mt-2 text-amber-600">
                  <AlertTriangle className="h-4 w-4 inline mr-1" />
                  Nota: Il DDT associato non verrà eliminato.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, item: null })}>{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={handleDelete}>{t("common.delete")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
